import { get, post, put, del } from '../client/axiosClient';
import { courtTypeEndpoints, COURT_TYPE_BASE_PATH } from '../endpoints/courtType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, EmptyResponse, ID } from '../types';
import type {
  CourtType, CourtTypeListItem,
  CreateCourtTypeRequest, UpdateCourtTypeRequest, CourtTypeSearchRequest,
} from '../types/courtType.types';

class CourtTypeService {
  async getAll(params?: CourtTypeSearchRequest): Promise<PaginatedResponse<CourtTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(COURT_TYPE_BASE_PATH, params)
      : courtTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<CourtType>> {
    const response = await get<ApiResponse<CourtType>>(courtTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<CourtType>> {
    const response = await get<ApiResponse<CourtType>>(courtTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateCourtTypeRequest): Promise<ApiResponse<CourtType>> {
    const response = await post<ApiResponse<CourtType>>(courtTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCourtTypeRequest): Promise<ApiResponse<CourtType>> {
    const response = await put<ApiResponse<CourtType>>(courtTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(courtTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${courtTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : courtTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const courtTypeService = new CourtTypeService();
export default courtTypeService;
